import React, { useState } from 'react'
import { router, usePage, Head } from '@inertiajs/react'
import { LockKeyhole, Loader2, Check, AlertCircle, Eye, EyeOff, Mail } from 'lucide-react'
import Marketing3AuthShell from '@/components/marketing3/Marketing3AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { FALLBACK_BRAND } from '@/lib/brand'
import '../marketing/marketing3.css'
import { csrfToken } from '@/lib/csrf'

// Every value here is composed by Users::InvitationsController#edit. The page only displays them.
interface AcceptInvitationProps {
  brand?: string | null
  invitationToken: string
  email: string
  businessName?: string | null
  invitedBy?: string | null
}

// Mirror of User::PASSWORD_MIN + the per-model complexity rule (app/models/user.rb). These are
// DISPLAY-ONLY hints; the backend Devise `:validatable` + strength validation is the source of truth.
const PASSWORD_MIN = 12

// mkt3 teal focus ring, see SignIn.tsx.
const FIELD_FOCUS = 'border-stone-200 focus-visible:ring-teal-600 focus-visible:ring-offset-0'

function passwordChecks(pw: string) {
  return [
    { label: `At least ${PASSWORD_MIN} characters`, ok: pw.length >= PASSWORD_MIN },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'A number', ok: /\d/.test(pw) },
  ]
}

export default function AcceptInvitation({
  brand: brandProp,
  invitationToken,
  email,
  businessName,
  invitedBy,
}: AcceptInvitationProps) {
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  const business = businessName?.trim() ? businessName : null

  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const checks = passwordChecks(password)
  const passwordValid = checks.every((c) => c.ok)
  const confirmMatches = confirm.length > 0 && confirm === password
  const canSubmit = passwordValid && confirmMatches && !submitting

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    // State-changing: router.put (the documented exception to the <Link> nav rule). devise_invitable
    // expects params nested under `user`, incl. the single-use token from the email link. The controller
    // answers 303: to the business on success, or back here with the reason on a password problem.
    router.put(
      '/users/invitation',
      {
        user: {
          invitation_token: invitationToken,
          password,
          password_confirmation: confirm,
        },
      },
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Marketing3AuthShell brand={brand}>
      <Head title="Accept invitation" />
      {/* Lead-in: who is asking, and for which business, so the person knows the link is genuine. */}
      <div className="text-center">
        <h1 className="mkt3-display mkt3-h3">{business ? `Join ${business}` : 'Accept your invitation'}</h1>
        <p className="mkt3-prose mx-auto mt-2 text-sm">
          {invitedBy ? (
            <>
              <span className="font-medium text-slate-700">{invitedBy}</span> invited you.{' '}
            </>
          ) : null}
          Choose a password to finish setting up your login.
        </p>
      </div>

      {flashAlert && (
        <div className="mt-6 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{flashAlert}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-7 space-y-5">
        {/* Email: fixed by the invitation. Read-only, and `username` so a password manager saves the pair. */}
        <div className="space-y-2">
          <Label htmlFor="email" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Email
          </Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={email}
              readOnly
              aria-readonly="true"
              autoComplete="username"
              className={`h-11 rounded-lg bg-stone-50 pl-9 text-ellipsis text-slate-600 ${FIELD_FOCUS}`}
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              placeholder="Create a strong password"
              className={`h-11 rounded-lg bg-white pr-10 ${FIELD_FOCUS}`}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* Live strength checklist, display only */}
          <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {checks.map((c) => (
              <li
                key={c.label}
                className={cn(
                  'flex items-center gap-1.5 text-xs transition-colors',
                  c.ok ? 'text-teal-700' : 'text-slate-500'
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-full transition-colors',
                    c.ok ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-slate-400'
                  )}
                >
                  <Check className="h-2.5 w-2.5" />
                </span>
                {c.label}
              </li>
            ))}
          </ul>
        </div>

        {/* Confirm */}
        <div className="space-y-2">
          <Label htmlFor="confirm" className="mkt3-label normal-case tracking-[0.12em] text-slate-500">
            Confirm password
          </Label>
          <Input
            id="confirm"
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            className={`h-11 rounded-lg bg-white ${FIELD_FOCUS}`}
          />
          {confirm.length > 0 && !confirmMatches && (
            <p className="text-xs text-rose-600">Passwords don&apos;t match.</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="mkt3-cta h-auto min-h-11 w-full whitespace-normal rounded-lg bg-teal-600 py-2.5 text-sm font-semibold text-white hover:bg-teal-700"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
              Setting up your login…
            </>
          ) : (
            <>
              <LockKeyhole className="mr-2 h-4 w-4 shrink-0" />
              {/* The heading already names the business, so the label stays short enough for one
                  line on a phone however long that name is. */}
              {business ? 'Set password and join' : 'Set password and sign in'}
            </>
          )}
        </Button>
      </form>
    </Marketing3AuthShell>
  )
}
